import { getContentType } from "@/helpers/documentPreview";

interface FileMetadata {
  contentType: string;
  contentLength: number;
  lastModified?: Date;
  etag?: string;
}

interface S3Config {
  bucketName: string;
  region: string;
  mainFolder: string;
}

// Default S3 configuration
const DEFAULT_S3_CONFIG: S3Config = {
  bucketName: "soe-storage",
  region: "ap-southeast-1",
  mainFolder: "images",
};

/**
 * Normalize filename by replacing spaces and encoded characters
 */
export function normalizeFileName(fileName: string): string {
  return decodeURIComponent(fileName)
    .replace(/%20/g, "_") // Replace %20 with underscore
    .replace(/\s+/g, "_"); // Replace any actual spaces with underscore
}

/**
 * Construct S3 file URL
 */
export function constructS3FileUrl(
  filename: string,
  config: Partial<S3Config> = {}
): string {
  const s3Config = { ...DEFAULT_S3_CONFIG, ...config };
  const normalizedFilename = normalizeFileName(filename);

  const baseUrl = `https://${s3Config.bucketName}.s3.${s3Config.region}.amazonaws.com`;
  return `${baseUrl}/${s3Config.mainFolder}/${normalizedFilename}`;
}

/**
 * Get file metadata from S3 (client-side approach using HEAD request)
 * Note: This requires CORS to be configured on the S3 bucket
 */
export async function getFileMetadata(
  fileUrl: string
): Promise<FileMetadata | null> {
  try {
    const response = await fetch(fileUrl, {
      method: "HEAD",
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentType =
      response.headers.get("Content-Type") || "application/octet-stream";
    const contentLength = parseInt(
      response.headers.get("Content-Length") || "0",
      10
    );
    const lastModified = response.headers.get("Last-Modified")
      ? new Date(response.headers.get("Last-Modified")!)
      : undefined;
    const etag = response.headers.get("ETag")?.replace(/"/g, "");

    return {
      contentType,
      contentLength,
      lastModified,
      etag,
    };
  } catch (error) {
    console.error("Error fetching file metadata:", error);

    // Fallback: determine content type from file extension
    const extension = fileUrl.split(".").pop()?.toLowerCase() || "";
    return {
      contentType: getContentType(extension),
      contentLength: 0,
    };
  }
}

/**
 * Get file metadata with filename normalization
 */
export async function getFileMetadataByKey(
  key: string,
  config: Partial<S3Config> = {}
): Promise<FileMetadata | null> {
  const fileUrl = constructS3FileUrl(key, config);
  return getFileMetadata(fileUrl);
}

/**
 * Format date in human-readable format
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/**
 * Get file icon emoji based on content type
 */
export function getFileIcon(contentType: string): string {
  const iconMap: { [key: string]: string } = {
    "application/pdf": "📄",
    "image/jpeg": "🖼️",
    "image/png": "🖼️",
    "image/gif": "🖼️",
    "image/webp": "🖼️",
    "text/plain": "📝",
    "text/markdown": "📝",
    "application/msword": "📄",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "📄",
    "application/vnd.ms-excel": "📊",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "📊",
    "application/vnd.ms-powerpoint": "📽️",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "📽️",
    "application/zip": "🗜️",
    "application/x-zip-compressed": "🗜️",
    "video/mp4": "🎥",
    "video/avi": "🎥",
    "video/mov": "🎥",
    "audio/mp3": "🎵",
    "audio/wav": "🎵",
    "audio/mpeg": "🎵",
  };
  return iconMap[contentType] || "📁";
}

/**
 * Parse S3 URL to extract bucket, region, and key information
 */
export function parseS3Url(url: string): {
  bucketName: string;
  region: string;
  key: string;
} | null {
  try {
    const s3UrlPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
    const match = url.match(s3UrlPattern);

    if (!match) {
      return null;
    }

    return {
      bucketName: match[1],
      region: match[2],
      key: match[3],
    };
  } catch (error) {
    console.error("Error parsing S3 URL:", error);
    return null;
  }
}

/**
 * Check if URL is a valid S3 URL
 */
export function isS3Url(url: string): boolean {
  return url.includes(".s3.") && url.includes(".amazonaws.com");
}

/**
 * Get file metadata with enhanced error handling and fallback
 */
export async function getEnhancedFileMetadata(
  fileUrlOrKey: string,
  config: Partial<S3Config> = {}
): Promise<FileMetadata | null> {
  try {
    let fileUrl: string;

    // Check if input is already a full URL or just a key
    if (fileUrlOrKey.startsWith("http")) {
      fileUrl = fileUrlOrKey;
    } else {
      fileUrl = constructS3FileUrl(fileUrlOrKey, config);
    }

    return await getFileMetadata(fileUrl);
  } catch (error) {
    console.error("Error getting enhanced file metadata:", error);
    return null;
  }
}
